import { municipalPropertyRegisterListQueryDocument } from "@/domain/municipal-property/hooks/api/municipal-property-register-list-query.gql";
import { MUNICIPAL_PROPERTY_YEAR_LIST_QUERY_DOCUMENT } from "@/domain/municipal-property/hooks/api/municipal-property-year-list-query.gql";
import { refreshMunicipalPropertyManagement } from "@/domain/municipal-property/lib/refresh-municipal-property-management";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { MUNICIPAL_PROPERTY_LIST_PAGE_SIZE } from "@ark/shared";
import { useQueryClient } from "@tanstack/react-query";

type RecoverMunicipalPropertyYearInput = { workspaceId: string; propertyId: string; inventoryId?: string } & (
  | { registerId: string; year?: never }
  | { registerId?: never; year: number }
);

export function useRecoverMunicipalPropertyYear(): (
  input: RecoverMunicipalPropertyYearInput,
) => Promise<string | null> {
  const queryClient = useQueryClient();
  return async function recoverMembership({ workspaceId, propertyId, registerId, year, inventoryId }) {
    let activeRegisterId = registerId;
    if (!activeRegisterId) {
      const response = await createOperationClient("MunicipalPropertyRegisterList").request(
        municipalPropertyRegisterListQueryDocument,
        { input: { workspaceId, year, first: 1 } },
      );
      activeRegisterId = response.municipalPropertyRegisterList.nodeList[0]?.id;
    }
    if (!activeRegisterId) return null;
    let after: string | undefined;
    do {
      const response = await createOperationClient("MunicipalPropertyRegisterContent").request(
        MUNICIPAL_PROPERTY_YEAR_LIST_QUERY_DOCUMENT,
        { registerId: activeRegisterId, inventoryId, first: MUNICIPAL_PROPERTY_LIST_PAGE_SIZE.default, after },
      );
      const page = response.municipalPropertyYearList;
      const membership = page.nodeList.find(
        (entry) => entry.propertyId === propertyId && entry.registerId === activeRegisterId,
      );
      if (membership) {
        await refreshMunicipalPropertyManagement({ queryClient, scope: { workspaceId, propertyIdList: [propertyId] } });
        return membership.id;
      }
      if (!page.hasNextPage) return null;
      if (!page.endCursor || page.endCursor === after) throw new Error("Annual property pagination did not advance");
      after = page.endCursor;
    } while (after);
    return null;
  };
}
